/**
 * Where a patient says where her pregnancy stands.
 *
 * Two steps, following the end-cycle dialog rather than inventing a shape: pick
 * one of three, then say as much or as little as you want about it. Choosing
 * advances, so the whole list of loss kinds is never on screen beside the choice
 * that produced it — which is what made the single-page version overflow.
 *
 * All the detail is optional. Save is live the moment step two opens, so someone
 * telling the app her pregnancy has ended can say so and stop. The kind of loss is
 * offered and never required: "missed miscarriage" and "anembryonic pregnancy" are
 * words a clinic says to you, so "I'm not sure" is the standing answer and the
 * record then says a pregnancy ended and claims nothing more. Ectopic is on the
 * list because it matters medically, not because anyone should have to identify it.
 *
 * There is no confirmation step. She does not say it twice.
 */

import { useCallback, useEffect, useState } from "react";
import { View, Pressable, ScrollView, useWindowDimensions } from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import {
  ActionRowCard,
  Dialog,
  DialogHeader,
  DialogIcon,
  DialogPrimaryButton,
  MoonNumericField,
  colors,
} from "@louise/moon-ui";
import { Heart, HeartCrack, Baby, ArrowLeft } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import type { MiscarriageOutcomeKind } from "@src/packages/cycles/hooks/useCycles";
import type { PregnancyStatus } from "@src/packages/cycles/presenters/pregnancyStatusUpdate";
import endingIllustration from "../../../../assets/images/end-treatment-cycle-illustration.png";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const NS = "care.update_pregnancy";

/** The three things that can be true, in the order they are offered. */
const STANDS = ["continuing", "ended", "birth"] as const;
type Stands = (typeof STANDS)[number];

const STAND_ICONS: Record<Stands, typeof Heart> = {
  continuing: Heart,
  ended: HeartCrack,
  birth: Baby,
};

/**
 * The kinds of loss, ordered by how likely a patient is to have been given the
 * words rather than by clinical taxonomy.
 */
const LOSS_KINDS: MiscarriageOutcomeKind[] = [
  "complete_miscarriage",
  "incomplete_miscarriage",
  "missed_miscarriage",
  "chemical_pregnancy",
  "ectopic_pregnancy",
  "anembryonic_pregnancy",
  "still_birth",
  "recurrent_miscarriage",
];

/**
 * A chip, styled as the end-cycle dialog styles its cancellation reasons:
 * neutral, with weight the only emphasis.
 *
 * Deliberately not a tinted green. Nothing on this screen is an achievement, and
 * the rest of the app does not colour a selection — the first version did both and
 * read as somebody else's design.
 */
function DetailChip({
  label,
  selected,
  onPress,
  testID,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
  testID?: string;
}) {
  const capturePress = usePressCapture("DetailChip");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(label), "pregnancy_detail_chip");
        onPress();
      }}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected }}
      aria-selected={selected}
      testID={testID}
      style={({ pressed }) => ({
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: selected ? "rgba(0,0,0,0.18)" : "rgba(0,0,0,0.08)",
        backgroundColor: selected
          ? "rgba(0,0,0,0.06)"
          : pressed
            ? "rgba(255,255,255,0.7)"
            : "rgba(255,255,255,0.5)",
      })}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          fontWeight: selected ? "600" : "500",
          color: colors.textMid,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

export default function UpdatePregnancyModal({
  visible,
  onClose,
  onSave,
  isSaving = false,
  error,
}: {
  visible: boolean;
  onClose: () => void;
  onSave: (status: PregnancyStatus) => void;
  isSaving?: boolean;
  error?: string | null;
}) {
  const t = useTranslations();
  const { height } = useWindowDimensions();
  // The same bounds the beta dialog uses, so neither can outgrow a short window.
  const dialogMaxHeight = Math.min(760, Math.max(480, height * 0.85));
  const [stands, setStands] = useState<Stands | null>(null);
  const [weeks, setWeeks] = useState<number | null>(null);
  const [lossKind, setLossKind] = useState<MiscarriageOutcomeKind | null>(null);
  const [births, setBirths] = useState<number | null>(null);

  const reset = useCallback(() => {
    setStands(null);
    setWeeks(null);
    setLossKind(null);
    setBirths(null);
  }, []);

  /**
   * Clear it whenever it closes, not only when she dismisses it.
   *
   * Both callers close on a successful save by hiding the dialog directly, which
   * never reaches `handleClose` — so the answers survived. Two weeks after saving
   * "still pregnant, 8 weeks" she reopened it already on step two, pre-answered,
   * with the three choices out of sight.
   */
  useEffect(() => {
    if (!visible) reset();
  }, [visible, reset]);

  const handleClose = useCallback(() => {
    if (isSaving) return;
    reset();
    onClose();
  }, [isSaving, onClose, reset]);

  const handleSave = useCallback(() => {
    if (!stands) return;
    if (stands === "continuing") {
      onSave({ stands: "continuing", weekCount: weeks });
      return;
    }
    if (stands === "ended") {
      onSave({ stands: "ended", kind: lossKind, weekCount: weeks });
      return;
    }
    onSave({
      stands: "birth",
      // More than one baby is the only thing the count can mean here.
      kind: births && births > 1 ? "multiple_live_birth" : "live_birth",
      liveBirthCount: births,
    });
  }, [births, lossKind, onSave, stands, weeks]);

  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      layout="centered"
      closeButton="minimal"
      testID="update-pregnancy-modal"
      containerStyle={{ maxWidth: 420, maxHeight: dialogMaxHeight }}
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{
        paddingTop: 20,
        paddingHorizontal: 22,
        paddingBottom: 18,
        maxHeight: dialogMaxHeight,
      }}
    >
      <View style={{ flex: 1, minHeight: 0 }}>
        <DialogIcon
          source={endingIllustration}
          width={56}
          height={62}
          style={{ marginBottom: 14 }}
        />
        <DialogHeader
          title={
            stands ? t(`${NS}.stands_${stands}`) : t(`${NS}.where_things_stand`)
          }
          subtitle={
            stands
              ? t(`${NS}.add_anything_you_want_to`)
              : t(`${NS}.tell_us_only_what_you_want_to`)
          }
        />

        <ScrollView
          contentContainerStyle={{ paddingTop: 14, paddingBottom: 4 }}
          showsVerticalScrollIndicator={false}
        >
          {!stands ? (
            <View style={{ gap: 10 }}>
              {STANDS.map((option) => {
                const Icon = STAND_ICONS[option];
                return (
                  <ActionRowCard
                    key={option}
                    leading={<Icon size={18} color={colors.textMid} />}
                    title={t(`${NS}.stands_${option}`)}
                    onPress={() => setStands(option)}
                    accessibilityLabel={t(`${NS}.stands_${option}`)}
                  />
                );
              })}
            </View>
          ) : (
            <View style={{ gap: 18 }}>
              {stands === "continuing" || stands === "ended" ? (
                <MoonNumericField
                  label={t(`${NS}.how_many_weeks_if_you_know`)}
                  value={weeks}
                  onChange={setWeeks}
                  placeholder={t(`${NS}.weeks_placeholder`)}
                  min={0}
                  // A pregnancy does not run past ~45 weeks, so a stray digit is a
                  // typo rather than a record.
                  max={45}
                  disabled={isSaving}
                  testID="update-pregnancy-weeks"
                />
              ) : null}

              {stands === "ended" ? (
                <View style={{ gap: 10 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      lineHeight: 19,
                      color: colors.textMid,
                    }}
                  >
                    {t(`${NS}.if_your_clinic_told_you_which`)}
                  </Text>
                  <View
                    style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                  >
                    {/* The standing answer, not a placeholder. */}
                    <DetailChip
                      label={t(`${NS}.i_am_not_sure`)}
                      selected={lossKind === null}
                      onPress={() => setLossKind(null)}
                      testID="update-pregnancy-loss-unknown"
                    />
                    {LOSS_KINDS.map((kind) => (
                      <DetailChip
                        key={kind}
                        label={t(`${NS}.loss_${kind}`)}
                        selected={lossKind === kind}
                        onPress={() => setLossKind(kind)}
                        testID={`update-pregnancy-loss-${kind}`}
                      />
                    ))}
                  </View>
                </View>
              ) : null}

              {stands === "birth" ? (
                <MoonNumericField
                  label={t(`${NS}.how_many_babies_if_you_want_to_say`)}
                  value={births}
                  onChange={setBirths}
                  placeholder={t(`${NS}.babies_placeholder`)}
                  min={1}
                  max={10}
                  disabled={isSaving}
                  testID="update-pregnancy-births"
                />
              ) : null}
            </View>
          )}

          {error ? (
            <Text
              className="font-dm-sans"
              style={{
                marginTop: 14,
                fontSize: 13,
                lineHeight: 19,
                color: colors.error,
              }}
            >
              {error}
            </Text>
          ) : null}
        </ScrollView>

        {/*
          Stacked, not paired. `DialogActions` puts two children in a row and
          expects them to share the width; a content-sized secondary next to a
          full-width primary rendered as a small grey blob. The end-cycle dialog's
          own "choose a different outcome" affordance is a full-width primary
          followed by an `ActionRowCard`, and this is the same move — go back and
          pick something else — so it uses the same shape.
        */}
        {stands ? (
          <View style={{ marginTop: 16 }}>
            <DialogPrimaryButton
              label={t(`${NS}.save`)}
              onPress={handleSave}
              loading={isSaving}
              disabled={isSaving}
              fullWidth
            />
            <View style={{ height: 12 }} />
            <ActionRowCard
              leading={<ArrowLeft size={18} color={colors.textMid} />}
              title={t(`${NS}.choose_a_different_answer`)}
              // Back to the choice clears what the last answer collected: a week
              // typed for "still pregnant" is not an answer about a birth.
              onPress={reset}
              accessibilityLabel={t(`${NS}.choose_a_different_answer`)}
            />
          </View>
        ) : null}
      </View>
    </Dialog>
  );
}
